import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { api, setAuthToken } from '../../lib/api.ts';
import { useCarrito } from '../carrito/useCarrito.ts';

interface LoginPayload {
  email: string;
  password: string;
}

interface LoginError {
  error?: string;
  message?: string;
  attempts_left?: number;
  cooloff_hours?: number;
}

export function useLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: LoginPayload) => {
      queryClient.removeQueries();
      const { data } = await api.post('/api/auth/login', payload);
      return data;
    },
    onSuccess: async (data) => {
      toast.success('Bienvenido de nuevo.');
      
      if (data?.token) {
        setAuthToken(data.token, data.refresh_token);
        
        // Guardar información del usuario para verificación de rol
        if (data?.user) {
          localStorage.setItem('tiendaya_user', JSON.stringify({
            is_merchant: data.user.user_type === 'merchant',
            is_superuser: data.user.is_superuser || false,
            user_type: data.user.user_type
          }));
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['auth-user'] });
      
      const match = location.pathname.match(/\/t\/([^\/]+)/);
      
      if (match && match[1]) {
        const storeSlug = match[1];
        await useCarrito.getState().syncWithBackend(storeSlug);
      }
      
      if (data?.user?.is_superuser) {
        navigate('/super-admin');
      } else if (data?.user?.user_type === 'merchant') {
        navigate('/panel');
      } else {
        navigate('/mi-cuenta');
      }
    },
    onError: (error: any) => {
      const errorData: LoginError = error.response?.data;
      
      // Cuenta bloqueada
      if (errorData?.error === 'account_locked') {
        return; // El componente manejará el modal
      }
      
      // Credenciales inválidas con intentos restantes
      if (errorData?.error === 'invalid_credentials' && errorData.attempts_left !== undefined) {
        return; // El componente mostrará el mensaje
      }
      
      // Error genérico
      const message = errorData?.detail ?? errorData?.message ?? 'Credenciales inválidas';
      toast.error(message);
    },
  });
}